import { useEffect, useState, useCallback } from "react";
import { Reviews } from "@/pages/Reviews";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/useSEO";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [opts, setOpts] = useState({});
  const [materials, setMaterials] = useState([]);
  const [finishes, setFinishes] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [turnarounds, setTurnarounds] = useState([]);
  const [shipping, setShipping] = useState([]);
  const [config, setConfig] = useState({ material_id: null, finish_id: null, size_id: null, turnaround_id: null, shipping_option_id: null });
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkFileId, setArtworkFileId] = useState(null);
  const [artworkFilename, setArtworkFilename] = useState(null);
  const [artworkUploading, setArtworkUploading] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const uploadArtwork = async () => {
    if (!artworkFile) return null;
    if (!user) { toast.error("Please sign in to upload artwork"); nav("/login"); return null; }
    setArtworkUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", artworkFile);
      const { data } = await api.post("/uploads/artwork", fd);
      setArtworkFileId(data.id);
      setArtworkFilename(data.original_filename);
      toast.success("Artwork uploaded");
      return data.id;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      return null;
    } finally { setArtworkUploading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      try {
        const [p, mats, fins, szs, turn, ship] = await Promise.all([
          api.get(`/products/${slug}`), api.get("/materials"), api.get("/finishes"),
          api.get("/sizes"), api.get("/turnaround-options"), api.get("/shipping-options"),
        ]);
        if (cancelled) return;
        setProduct(p.data); setQty(p.data.min_quantity || 1);
        setMaterials(mats.data.filter(x => x.active));
        setFinishes(fins.data.filter(x => x.active));
        setSizes(szs.data.filter(x => x.active));
        setTurnarounds(turn.data.filter(x => x.active));
        setShipping(ship.data.filter(x => x.active));
      } catch (err) {
        if (!cancelled) logger.error("Product load failed:", err);
      }
    };
    loadAll();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    setPricingLoading(true);
    api.post("/pricing/quote", { product_id: product.id, quantity: qty, options: opts, ...config })
      .then(r => { if (!cancelled) setPricing(r.data); })
      .catch(err => { if (!cancelled) { logger.error("Pricing failed:", err); setPricing(null); } })
      .finally(() => { if (!cancelled) setPricingLoading(false); });
    return () => { cancelled = true; };
  }, [product, qty, opts, config]);

  // SEO — always call (before any early-return) to satisfy the rules of hooks
  useSEO(product ? {
    title: product.name,
    description: (product.description || "").slice(0, 155) || `${product.name} — premium printing from BOLAWPRESS.`,
    image: product.image_url,
    type: "product",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Shop", url: "/shop" },
      { name: product.name, url: `/product/${slug}` },
    ],
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description || "",
      image: product.image_url ? [product.image_url] : undefined,
      brand: { "@type": "Brand", name: "BOLAWPRESS" },
      category: (product.parent_type || "").replace("_", " "),
      offers: {
        "@type": "Offer",
        price: (pricing ? pricing.total : product.base_price).toFixed(2),
        priceCurrency: "USD",
        availability: product.available !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        url: typeof window !== "undefined" ? window.location.href : "",
      },
    },
  } : {});

  if (!product) return <div className="p-20 text-center font-mono text-sm tracking-widest">LOADING...</div>;

  const chip = (selected) => selected ? "border-[#C5A059] text-[#0a0a0a] bg-[#FAFAFA]" : "border-[#E5E5E5] text-[#555] hover:border-[#0a0a0a]";
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-2 gap-12">
      <div className="aspect-square bg-[#FAFAFA] overflow-hidden md:sticky md:top-24 h-fit">
        <img src={product.image_url} className="w-full h-full object-cover" alt={product.name} />
      </div>
      <div>
        <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">{product.parent_type.replace("_", " ")}</p>
        <h1 className="font-serif text-4xl md:text-6xl mt-3">{product.name}</h1>
        <p className="font-mono text-2xl mt-4 text-[#0a0a0a]" data-testid="product-live-price">
          {pricingLoading ? "…" : pricing ? `$${pricing.total.toFixed(2)}` : `$${product.base_price.toFixed(2)}`}
          {pricing && <span className="text-xs text-[#888] ml-2">(${pricing.unit_price.toFixed(2)}/unit)</span>}
        </p>
        <p className="mt-6 text-[#555] leading-relaxed">{product.description}</p>

        {materials.length > 0 && (
          <div className="mt-8">
            <p className="text-xs font-mono tracking-[0.3em] uppercase mb-3">Material</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="material-none" onClick={() => setConfig(c => ({ ...c, material_id: null }))} className={`px-4 py-2 border text-sm ${chip(!config.material_id)}`}>Default</button>
              {materials.map(m => (
                <button key={m.id} data-testid={`material-${m.id}`} onClick={() => setConfig(c => ({ ...c, material_id: m.id }))} className={`px-4 py-2 border text-sm ${chip(config.material_id === m.id)}`}>
                  {m.name}{m.price_delta ? <span className="text-[10px] font-mono text-[#C5A059] ml-1">{m.price_delta > 0 ? "+" : ""}${m.price_delta}</span> : null}
                </button>
              ))}
            </div>
          </div>
        )}

        {finishes.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-mono tracking-[0.3em] uppercase mb-3">Finish</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="finish-none" onClick={() => setConfig(c => ({ ...c, finish_id: null }))} className={`px-4 py-2 border text-sm ${chip(!config.finish_id)}`}>Default</button>
              {finishes.map(f => (
                <button key={f.id} data-testid={`finish-${f.id}`} onClick={() => setConfig(c => ({ ...c, finish_id: f.id }))} className={`px-4 py-2 border text-sm ${chip(config.finish_id === f.id)}`}>
                  {f.name}{f.price_delta ? <span className="text-[10px] font-mono text-[#C5A059] ml-1">{f.price_delta > 0 ? "+" : ""}${f.price_delta}</span> : null}
                </button>
              ))}
            </div>
          </div>
        )}

        {sizes.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-mono tracking-[0.3em] uppercase mb-3">Size</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="size-none" onClick={() => setConfig(c => ({ ...c, size_id: null }))} className={`px-4 py-2 border text-sm ${chip(!config.size_id)}`}>Default</button>
              {sizes.map(s => (
                <button key={s.id} data-testid={`size-${s.id}`} onClick={() => setConfig(c => ({ ...c, size_id: s.id }))} className={`px-4 py-2 border text-sm ${chip(config.size_id === s.id)}`}>
                  {s.name} <span className="text-[10px] font-mono text-[#888]">({s.dimensions})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {turnarounds.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-mono tracking-[0.3em] uppercase mb-3">Turnaround</p>
            <div className="flex flex-wrap gap-2">
              {turnarounds.map(t => (
                <button key={t.id} data-testid={`turnaround-${t.id}`} onClick={() => setConfig(c => ({ ...c, turnaround_id: t.id }))} className={`px-4 py-2 border text-sm ${chip(config.turnaround_id === t.id)}`}>
                  {t.name}{t.price_multiplier !== 1 ? <span className="text-[10px] font-mono text-[#C5A059] ml-1">×{t.price_multiplier}</span> : null}
                </button>
              ))}
            </div>
          </div>
        )}

        {shipping.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-mono tracking-[0.3em] uppercase mb-3">Shipping</p>
            <div className="flex flex-wrap gap-2">
              {shipping.map(s => (
                <button key={s.id} data-testid={`shipping-${s.id}`} onClick={() => setConfig(c => ({ ...c, shipping_option_id: s.id }))} className={`px-4 py-2 border text-sm ${chip(config.shipping_option_id === s.id)}`}>
                  {s.name} <span className="text-[10px] font-mono text-[#888]">{s.base_price > 0 ? `$${s.base_price}` : "free"}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {product.options && product.options.length > 0 && (
          <div className="mt-6 space-y-6">
            {product.options.map(o => (
              <div key={o.name}>
                <p className="text-xs font-mono tracking-[0.3em] uppercase mb-3">{o.name}</p>
                <div className="flex flex-wrap gap-2">
                  {o.values.map(v => (
                    <button key={v} data-testid={`opt-${o.name}-${v}`} onClick={() => setOpts(prev => ({ ...prev, [o.name]: v }))} className={`px-4 py-2 border text-sm ${chip(opts[o.name] === v)}`}>{v}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex items-center gap-3">
          <p className="text-xs font-mono tracking-[0.3em] uppercase">Quantity</p>
          <input type="number" min={product.min_quantity || 1} value={qty} onChange={e => setQty(Math.max(1, Number(e.target.value)))} data-testid="product-qty" className="w-24 border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-2 font-mono focus:outline-none focus:border-[#C5A059]" />
        </div>

        {pricing && pricing.breakdown && (
          <details className="mt-6 border border-[#E5E5E5] p-4">
            <summary className="cursor-pointer text-xs font-mono tracking-[0.25em] uppercase">Price Breakdown</summary>
            <div className="mt-3 space-y-1 text-sm">
              {pricing.breakdown.map((b) => (
                <div key={`${b.label}-${b.amount}`} className="flex justify-between font-mono text-xs">
                  <span className="text-[#555]">{b.label}</span>
                  <span className={b.amount < 0 ? "text-[#1F4D2A]" : "text-[#0a0a0a]"}>${b.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-[#E5E5E5] pt-2 mt-2 flex justify-between font-mono">
                <span>Total</span><span className="text-[#C5A059]">${pricing.total.toFixed(2)}</span>
              </div>
            </div>
          </details>
        )}

        {/* Artwork upload (pre-cart, optional) */}
        <div className="mt-8 border border-[#E5E5E5] p-5" data-testid="product-artwork-block">
          <div className="flex items-baseline justify-between">
            <p className="text-xs font-mono tracking-[0.3em] uppercase">Your Artwork <span className="text-[#888] normal-case tracking-normal">(optional — you can also upload later from your dashboard)</span></p>
          </div>
          <p className="mt-2 text-xs text-[#888]">Accepted: PDF, PNG, JPG, SVG, AI, EPS, PSD, TIFF, ZIP · Max 50MB</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="inline-block text-xs font-mono tracking-[0.25em] uppercase border border-[#0a0a0a] px-4 py-3 cursor-pointer hover:bg-[#FAFAFA]" data-testid="product-artwork-label">
              {artworkFile ? artworkFile.name.slice(0, 32) : (artworkFilename || "Choose File")}
              <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.svg,.ai,.eps,.psd,.zip,.tiff,.tif,.gif,.webp" onChange={e => { setArtworkFile(e.target.files[0] || null); setArtworkFileId(null); setArtworkFilename(null); }} data-testid="product-artwork-input" />
            </label>
            {artworkFile && !artworkFileId && (
              <button type="button" onClick={uploadArtwork} disabled={artworkUploading} data-testid="product-artwork-upload"
                className="text-xs font-mono tracking-[0.25em] uppercase bg-[#0a0a0a] text-white px-4 py-3 disabled:opacity-50">
                {artworkUploading ? "Uploading..." : "Upload"}
              </button>
            )}
            {artworkFileId && <span className="text-xs text-[#1F4D2A] font-mono" data-testid="product-artwork-status">✓ {artworkFilename}</span>}
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button data-testid="product-add-cart" disabled={!product.available}
            onClick={async () => {
              let fileId = artworkFileId;
              let fileName = artworkFilename;
              if (artworkFile && !fileId) {
                if (!user) { toast.error("Please sign in to attach artwork, or add to cart without it"); return; }
                fileId = await uploadArtwork();
                fileName = artworkFile.name;
                if (!fileId) return;
              }
              add(
                { ...product, base_price: pricing ? pricing.total / qty : product.base_price },
                qty,
                { ...opts, ...(config.finish_id ? { _finish_id: config.finish_id } : {}) },
                { artwork_file_id: fileId || null, artwork_filename: fileName || null }
              );
              toast.success("Added to cart");
            }}
            className="flex-1 bg-[#0a0a0a] text-white hover:bg-[#C5A059] hover:text-[#0a0a0a] transition-colors px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold disabled:opacity-40">
            Add to Cart
          </button>
          <Link to={`/designer?product=${product.slug}`} data-testid="product-design-btn" className="px-6 py-4 border border-[#C5A059] text-[#C5A059] text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#C5A059] hover:text-[#0a0a0a]">Design Online</Link>
          <button onClick={async () => { try { const { data } = await api.post(`/wishlist/${product.id}`); toast.success(data.in_wishlist ? "Added to wishlist" : "Removed from wishlist"); } catch { toast.error("Sign in to save"); } }} data-testid="product-wishlist-btn" className="px-4 py-4 border border-[#0a0a0a] text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#FAFAFA]">♡</button>
        </div>

        <Reviews product_id={product.id} />

        <p className="mt-8 text-xs font-mono text-[#888] tracking-wider">SKU · {product.slug.toUpperCase()} · Lead time {product.lead_time_days} days</p>
      </div>
    </section>
  );
}
